import { useEffect, useState } from 'react';
import { Outlet, useNavigate, useRouterState } from '@tanstack/react-router';
import { Shell } from '@atlas/ui';
import { CommandPalette } from './command-palette';
import { NAV_ITEMS } from './navigation';
import { EvidenceDrawer } from '../components/EvidenceDrawer';
import { EvidenceProvider } from '../components/EvidenceContext';
import { ROUTE_CHORDS } from './navigation';
import { isAuthBypassed } from '../lib/auth';

export function AppLayout() {
  const navigate = useNavigate();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [navCollapsed, setNavCollapsed] = useState(() => {
    return window.localStorage.getItem('atlas-nav-collapsed') === 'true';
  });
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerTitle, setDrawerTitle] = useState('Evidence');
  const [drawerBody, setDrawerBody] = useState('');
  const [density, setDensity] = useState<'comfortable' | 'compact'>(() => {
    const stored = window.localStorage.getItem('atlas-density');
    return stored === 'compact' ? 'compact' : 'comfortable';
  });

  useEffect(() => {
    let chordPrefix = false;
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setPaletteOpen(true);
        return;
      }
      if (e.key.toLowerCase() === 'g' && !e.metaKey && !e.ctrlKey && !e.altKey) {
        chordPrefix = true;
        return;
      }
      if (chordPrefix && !e.metaKey && !e.ctrlKey) {
        chordPrefix = false;
        const path = ROUTE_CHORDS[e.key.toUpperCase()];
        if (path) {
          e.preventDefault();
          navigate({ to: path });
        }
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [navigate]);

  return (
    <>
      <Shell
        navItems={[...NAV_ITEMS]}
        currentPath={pathname}
        onNavigate={(path) => navigate({ to: path })}
        onCommandPalette={() => setPaletteOpen(true)}
        density={density}
        onDensityChange={(nextDensity) => {
          setDensity(nextDensity);
          window.localStorage.setItem('atlas-density', nextDensity);
        }}
        navCollapsed={navCollapsed}
        onNavCollapsedChange={(collapsed) => {
          setNavCollapsed(collapsed);
          window.localStorage.setItem('atlas-nav-collapsed', String(collapsed));
        }}
        devBanner={
          isAuthBypassed() ? (
            <div className="atlas-auth-banner" role="status">
              Authentication bypass is enabled for local development. No live portfolio data is loaded.
            </div>
          ) : undefined
        }
      >
        <EvidenceProvider
          onOpen={(title, body) => {
            setDrawerTitle(title);
            setDrawerBody(body);
            setDrawerOpen(true);
          }}
        >
          <Outlet />
        </EvidenceProvider>
      </Shell>
      <EvidenceDrawer
        open={drawerOpen}
        title={drawerTitle}
        body={drawerBody}
        onClose={() => setDrawerOpen(false)}
      />
      {paletteOpen && <CommandPalette onClose={() => setPaletteOpen(false)} />}
    </>
  );
}
